import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex } from '@chakra-ui/react';
import { ColumnDef, flexRender, getCoreRowModel, Row, useReactTable, VisibilityState } from '@tanstack/react-table';

type QuoteScheduleDataTableProps<Data extends object> = {
  columns: ColumnDef<Data, any>[];
  data: Data[];
  dataCount?: number;
  columnVisibility?: VisibilityState;
  isRowDisabled?: (row: Row<Data>['original']) => boolean;
  onRowClick?: (row: Row<Data>['original']) => void;
  hidePagination?: boolean;
  variant?: number;
  maxH?: BoxProps['maxH'];
};

export const QuoteScheduleDataTable = <Data extends object>({
  columns,
  data,
  columnVisibility,
  isRowDisabled,
  onRowClick,
  maxH,
  variant = 14,
}: QuoteScheduleDataTableProps<Data>) => {
  const table = useReactTable({
    columns,
    data: data ?? [],
    enableSorting: false,
    state: {
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroup = table.getHeaderGroups()[0];
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const gridTemplateColumns =
    visibleColumnCount === 7
      ? '2.8fr 1.1fr 1.35fr 1.8fr 1.7fr 1.2fr 1fr'
      : `repeat(${Math.max(visibleColumnCount, 1)}, minmax(0, 1fr))`;

  return (
    <Box
      w="full"
      maxW="full"
      h={maxH === 'full' ? 'full' : 'auto'}
      maxH={maxH}
      overflowX="auto"
      overflowY="auto"
      bg="bg.default"
    >
      <Box position="sticky" top={0} zIndex={1} bg='gray.100' borderWidth={1} borderColor="border.light">
        <Box
          display="grid"
          gridTemplateColumns={gridTemplateColumns}
          w="full"
          minW="container.xl"
          px={4}
          py={3}
          columnGap={8}
          alignItems="center"
        >
          {headerGroup?.headers.map((header) => {
            if (header.isPlaceholder) return null;
            return (
              <Flex
                key={header.id}
                align="center"
                gap={1}
                minW={0}
                color="text.paragraph"
                fontSize={12}
                lineHeight={4}
                fontWeight={600}
                textTransform="uppercase"
                bg="bg.subtle"
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </Flex>
            );
          })}
        </Box>
      </Box>

      <Box p={4} w="full" minW="container.xl">
        {table.getRowModel().rows.map((row) => (
          <Box
            key={row.id}
            display="grid"
            gridTemplateColumns={gridTemplateColumns}
            columnGap={8}
            alignItems="center"
            bg="bg.surface"
            borderWidth={1}
            borderColor="border.default"
            borderRadius="lg"
            mb={row.index === table.getRowModel().rows.length - 1 ? 0 : 2}
            py="7px"
            fontSize={variant}
            fontWeight={400}
            lineHeight={5}
            cursor={isRowDisabled?.(row.original) ? 'not-allowed' : 'pointer'}
            opacity={isRowDisabled?.(row.original) ? 0.5 : 1}
            _hover={{
              borderColor: isRowDisabled?.(row.original) ? 'border.default' : 'border.subtle',
            }}
            onClick={() => (isRowDisabled?.(row.original) ? undefined : onRowClick?.(row.original))}
          >
            {row.getVisibleCells().map((cell) => (
              <Box key={cell.id} minW={0} >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
